/**
 * Intake BE(spnp) `/v1/datasets/{uuid}/probes` · `/v1/probes/{uuid}` 경계.
 *
 * **접수는 열린 Probe 하나로 합쳐진다.** 진행 중(`requested` · `collecting`)인 건이 있으면
 * 서버가 `200` 으로 그 건을 주고, 종료된 뒤의 요청은 `201` 로 **새 Probe** 를 만든다 — 그래서
 * 상태 새로고침에 이 POST 를 쓸 수 없다. 이력은 `listProbes`, 진행 관측은 `getProbe` 폴링이다.
 *
 * `selected_source_ids` 는 세 상태가 서로 다른 뜻이라 `?? []` 로 접지 않는다 —
 * `null`(아직 고르지 않음) · `[]`(고른 것이 없음) · 값(그 목록).
 *
 * Owns:
 * - 요청 본문 구성, 응답 모양 확인과 `Probe` · `Revision` 변환, 201/200 구분.
 *
 * Does not own:
 * - 폴링 주기 · 수명 — `feature/probePoller.ts`. 상태 · 실패 문구 — 같은 폴더의 feature.
 */
import {
  apiClient,
  IntakeContractError,
  readBoundedString,
  readInstantMs,
  readInteger,
  readNullableString,
  readRecord,
  readString,
} from "@/shared/http";
import { PROBE_STATES, type Probe, type Revision, type RevisionKind } from "../domain/probe";

const RESOURCE = "probe";
const REVISION_KINDS: readonly RevisionKind[] = ["listing_digest", "commit_sha"];

export type RequestProbeOutcome = {
  probe: Probe;
  /** 이번 호출이 새 Probe 를 만들었는지(`201`). `false` 면 이미 진행 중이던 건이다(`200`). */
  created: boolean;
};

export async function requestProbe(
  datasetUuid: string,
  episodeTarget: number,
): Promise<RequestProbeOutcome> {
  // 본문 없이 보내면 422 다 — 값을 바꾸지 않을 때도 객체를 싣는다.
  const response = await apiClient.post<unknown>(`/v1/datasets/${datasetUuid}/probes`, {
    episode_target: episodeTarget,
  });

  return { probe: parseProbe(response.data), created: response.status === 201 };
}

export async function getProbe(probeUuid: string): Promise<Probe> {
  const response = await apiClient.get<unknown>(`/v1/probes/${probeUuid}`);
  return parseProbe(response.data);
}

/** 데이터셋의 Probe 이력 — 서버가 최신순으로 준다. 없으면 404 가 아니라 빈 목록이다. */
export async function listProbes(datasetUuid: string): Promise<readonly Probe[]> {
  const response = await apiClient.get<unknown>(`/v1/datasets/${datasetUuid}/probes`);
  const body = readRecord(response.data, RESOURCE, "body");
  if (!Array.isArray(body.probes)) {
    throw new IntakeContractError(RESOURCE, "probes");
  }

  return body.probes.map(parseProbe);
}

function parseProbe(value: unknown): Probe {
  const row = readRecord(value, RESOURCE, "row");

  return {
    uuid: readString(row.uuid, RESOURCE, "uuid"),
    datasetUuid: readString(row.dataset_uuid, RESOURCE, "dataset_uuid"),
    state: readBoundedString(row.state, PROBE_STATES, RESOURCE, "state"),
    episodeTarget: readInteger(row.episode_target, RESOURCE, "episode_target"),
    selectedSourceIds: parseSelectedSourceIds(row.selected_source_ids),
    sampleRef: readNullableString(row.sample_ref, RESOURCE, "sample_ref"),
    manifestDigest: readNullableString(row.manifest_digest, RESOURCE, "manifest_digest"),
    failureCode: readNullableString(row.failure_code, RESOURCE, "failure_code"),
    failureDetail: readNullableString(row.failure_detail, RESOURCE, "failure_detail"),
    revision: parseRevision(row.revision),
    createdAtMs: readInstantMs(row.created_at, RESOURCE, "created_at"),
  };
}

function parseSelectedSourceIds(value: unknown): readonly string[] | null {
  if (value === null) {
    return null;
  }
  if (!Array.isArray(value)) {
    throw new IntakeContractError(RESOURCE, "selected_source_ids");
  }

  return value.map((entry) => readString(entry, RESOURCE, "selected_source_ids[]"));
}

function parseRevision(value: unknown): Revision | null {
  if (value === null) {
    return null;
  }

  const row = readRecord(value, RESOURCE, "revision");
  return {
    uuid: readString(row.uuid, RESOURCE, "revision.uuid"),
    kind: readBoundedString(row.kind, REVISION_KINDS, RESOURCE, "revision.kind"),
    identifier: readString(row.identifier, RESOURCE, "revision.identifier"),
    fileCount: readInteger(row.file_count, RESOURCE, "revision.file_count"),
    totalBytes: readInteger(row.total_bytes, RESOURCE, "revision.total_bytes"),
    listedAtMs: readInstantMs(row.listed_at, RESOURCE, "revision.listed_at"),
  };
}
